import { useCallback, useEffect, useRef, useState } from 'react';
import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { type UpsertAudience, useCreateSegment } from '@frontend/api-bulk-messaging';
import { BulkMessageHooks } from '@frontend/bulk-message-service';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useBulkMessageEditorContext } from '../../../bulk-message-editor.context';
import { useBulkUrlData } from '../../../hooks/use-bulk-url-data';

type Props = {
  createHiddenDraft: () => Promise<string | undefined>;
  upsertAudience: UpsertAudience;
};

const normalizeRecipients = (recipients: Recipient[], campaignId: string) =>
  recipients.map((recipient) => ({
    ...recipient,
    campaignId,
  }));

export const usePassedRecipients = ({ createHiddenDraft, upsertAudience }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { selectedOrgId: orgId } = useAppScopeStore();
  const alert = useAlert();
  const { isUsingPassedRecipients } = useBulkUrlData();
  const { campaignId } = useBulkMessageEditorContext();
  const { mutateAsync: createSegment } = useCreateSegment();
  const { passedRecipients } = BulkMessageHooks.usePassedRecipientsStore();
  const [isProcessingPassedRecipients, setIsProcessingPassedRecipients] = useState(false);
  const isProcessingRef = useRef(false);

  const passedRecipientsRef = useRef(passedRecipients);
  passedRecipientsRef.current = passedRecipients;

  const processPassedRecipients = useCallback(async () => {
    if (isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;
    setIsProcessingPassedRecipients(true);

    let attempt = 0;
    let success = false;

    while (!success && attempt < 3) {
      try {
        attempt += 1;
        const currentRecipients = passedRecipientsRef.current;

        if (!campaignId) {
          const newCampaignId = await createHiddenDraft();
          if (!newCampaignId) throw new Error('Failed to create hidden draft campaign');

          const recipients = normalizeRecipients(currentRecipients, newCampaignId);
          await createSegment({
            campaignId: newCampaignId,
            orgId,
            segment: { segmentId: '', recipients },
          });
        } else {
          const recipients = normalizeRecipients(currentRecipients, campaignId);
          await upsertAudience({ recipients });
        }

        success = true;
        BulkMessageHooks.usePassedRecipientsStore.reset();
      } catch (error) {
        console.error(`Failed to create campaign or segment with passed recipients. Attempt number: ${attempt}`, error);

        if (attempt >= 3) {
          alert.error(t('Failed to use selected recipients. Please try again.'));
          BulkMessageHooks.usePassedRecipientsStore.reset();
        }
      }
      // TODO: Figure out how to update the audience step to immediately reflect the new audience
    }

    isProcessingRef.current = false;
    setIsProcessingPassedRecipients(false);
  }, [alert, campaignId, createHiddenDraft, createSegment, orgId, t, upsertAudience]);

  useEffect(() => {
    if (isUsingPassedRecipients && passedRecipients.length > 0 && !isProcessingRef.current) {
      void processPassedRecipients();
    }
  }, [isUsingPassedRecipients, passedRecipients.length, processPassedRecipients]);

  return {
    isProcessingPassedRecipients,
  };
};
